import taxStatusEnum from '@constants/enums/taxStatus';

import ReportApi from '@packages/reports/api';

import { yearsAndMonths } from '@utils/date';

import clock from '@plugins/dayjs';
import i18n from '@plugins/i18n';

import { Collection } from '@/models';

export class ReportDisposalsCollection extends Collection {
    static type = 'ReportDisposalsCollection';
    static endpoint = new ReportApi().methods.getInvestorDisposals;

    static fields() {
        return {
            adviser_id: this.string(null).nullable(),
            investor_id: this.string(null).nullable(),
            disposal_type: this.string(null).nullable(),
            tax_year: this.string(null).nullable()
        };
    }

    get preset_filters() {
        let filters = {};

        if (this.adviser_id) {
            filters.adviser_id = this.filterOperator('is') + ':' + this.adviser_id;
        }

        if (this.investor_id) {
            filters.investor_id = this.filterOperator('is') + ':' + this.investor_id;
        }

        if (this.tax_year) {
            filters.tax_year = this.filterOperator('is') + ':' + this.tax_year;
        }

        if (this.disposal_type) {
            filters.disposal_type = this.filterOperator('is') + ':' + this.disposal_type;
        }

        return filters;
    }

    get money_format_options() {
        return {
            decimals: 2,
            abbreviate: false
        };
    }

    get headers() {
        let headers = [
            this.createHeader('product', i18n.t('name'), {
                width: '18rem',
                minWidth: '18rem',
                sortable: true,
                filterable: true
            }),
            this.createHeader('fund', i18n.t('fund'), {
                width: '18rem',
                minWidth: '18rem',
                sortable: true,
                filterable: true
            }),
            this.createHeader('tranche', i18n.t('tranche'), {
                width: '18rem',
                minWidth: '18rem',
                sortable: true,
                filterable: true
            }),
            this.createHeader('investor', i18n.t('investor'), {
                width: '18rem',
                minWidth: '18rem',
                sortable: true,
                filterable: true
            }),
            this.createHeader('type', i18n.t('type'), {
                format: 'enum',
                enum: taxStatusEnum,
                enumA11yKey: 'enums.tax_status_short',
                width: '6rem'
            }),
            this.createHeader('purchase_cost', i18n.t('purchase_cost'), {
                format: 'money',
                formatOptions: this.money_format_options,
                width: '10rem'
            }),
            this.createHeader('share_issue_on', i18n.t('share_issue_on'), {
                format: 'date',
                width: '11rem'
            }),
            this.createHeader('applicable_tax_relief', i18n.t('applicable_tax_relief'), {
                format: 'money',
                formatOptions: this.money_format_options,
                width: '12rem'
            }),
            this.createHeader('tax_year', i18n.t('disposal_tax_year'), {
                width: '11rem',
                transform: data => this.getTaxYear(data.item.tax_year, { abbreviate: true }),
                sortable: true
            }),
            this.createHeader('duration_of_holding', i18n.t('duration_of_holding'), {
                width: '12rem',
                transform: data => {
                    try {
                        const transaction_date = clock(data.item.transaction_date);
                        const share_date = clock(data.item.share_issue_on);
                        return yearsAndMonths(share_date, transaction_date);
                    } catch (error) {
                        return null;
                    }
                },
                sortable: true
            }),
            this.createHeader('share_quantity', i18n.t('share_qty'), {
                format: 'number',
                width: '11rem'
            }),
            this.createHeader('transaction_date', i18n.t('transaction_date'), {
                format: 'date',
                width: '11rem'
            }),
            this.createHeader('amount', i18n.t('sale_proceeds'), {
                format: 'money',
                width: '10rem',
                formatOptions: this.money_format_options
            })
            // this.createHeader('disposal_letter', i18n.t('disposal_letter'), {
            //     width: '10rem',
            //     format: 'boolean',
            //     sortable: true
            // }),
            // this.createHeader('disposal_letter_on', i18n.t('disposal_letter_date'), {
            //     width: '12rem',
            //     format: 'date'
            // })
        ];

        return headers;
    }

    get default_headers() {
        return [
            'product',
            'fund',
            'tranche',
            'type',
            'investor',
            'purchase_cost',
            'share_issue_on',
            'applicable_tax_relief',
            'tax_year',
            'duration_of_holding',
            'share_quantity',
            'transaction_date',
            'amount',
            'disposal_letter',
            'disposal_letter_on'
        ];
    }

    get investor_headers() {
        return [
            'product',
            'fund',
            'tranche',
            'type',
            'purchase_cost',
            'share_issue_on',
            'applicable_tax_relief',
            'tax_year',
            'duration_of_holding',
            'share_quantity',
            'transaction_date',
            'amount',
            'disposal_letter',
            'disposal_letter_on'
        ];
    }
}

export default ReportDisposalsCollection;
